/**
 * Sales mix panel state + preview — aligned with app/utils/menuBuilderSalesMixPanelData.js.
 */

import { getMenuSalesMixDisplay } from 'src/utils/menuSalesMixDisplay'
import {
  buildFullSalesMixEntries,
  buildSalesMixQtyDraft,
  calculateMenuSalesMixMetrics,
  canonicalRecipeId,
  getRecipeId,
  readQtyForRecipe,
  recipeHasSalesMixPricing,
  resolveRecipeGpPercent,
} from 'src/utils/menuSalesMix'

export const buildInitialSalesMixQtyByRecipe = (initialSalesMix = []) =>
  buildSalesMixQtyDraft(initialSalesMix)

export const pickQtyByRecipeForPanelRecipes = (
  panelRecipes = [],
  qtyByRecipe = {},
) => {
  const draft = {}

  for (const recipe of panelRecipes) {
    const id = recipe?.id
    if (!id) {
      continue
    }

    const qty = readQtyForRecipe(qtyByRecipe, id)
    if (qty !== '' && qty != null) {
      draft[id] = String(qty)
    }
  }

  return draft
}

export const buildSalesMixSaveEntries = (panelRecipes = [], qtyByRecipe = {}) =>
  panelRecipes
    .filter(recipe => recipe?.id)
    .map(recipe => ({
      recipeId: getRecipeId(recipe.sourceRecipe) || recipe.id,
      expectedQty: Number(readQtyForRecipe(qtyByRecipe, recipe.id)) || 0,
    }))

export const buildMenuSalesMixPanelRecipes = ({
  sections = [],
  getRecipesBySection = () => [],
} = {}) => {
  const rows = []
  const seen = new Set()
  const sectionNameById = Object.fromEntries(
    sections.map(section => [String(section._id), section.name]),
  )

  sections.forEach(section => {
    getRecipesBySection(section._id).forEach(menuRecipe => {
      const recipe = menuRecipe?.recipe ?? menuRecipe
      const id = canonicalRecipeId(recipe) || getRecipeId(recipe)
      if (!id || seen.has(id)) {
        return
      }

      seen.add(id)
      const gpPercent = resolveRecipeGpPercent(recipe)

      rows.push({
        id,
        title: recipe?.title || 'Untitled recipe',
        sectionName: sectionNameById[String(section._id)] || '',
        gpPercent: gpPercent != null ? Math.round(gpPercent) : null,
        hasPricing: recipeHasSalesMixPricing(recipe),
        sourceRecipe: recipe,
      })
    })
  })

  return rows
}

export const buildMenuSalesMixPanelPreview = ({
  panelRecipes = [],
  qtyByRecipe = {},
} = {}) => {
  const scopedQty = pickQtyByRecipeForPanelRecipes(panelRecipes, qtyByRecipe)
  const recipeIds = panelRecipes.map(recipe => recipe.id)
  const entries = buildFullSalesMixEntries(recipeIds, scopedQty)
  const metrics = calculateMenuSalesMixMetrics({
    recipes: panelRecipes.map(recipe => recipe.sourceRecipe).filter(Boolean),
    salesMix: entries,
  })
  const display = getMenuSalesMixDisplay(metrics)

  return {
    hasSalesMix: metrics.hasSalesMix,
    weightedGp: display.weightedGp,
    estimatedTotalGp: metrics.estimatedTotalGp,
    estimatedTotalGpLabel: display.estimatedTotalGpLabel,
  }
}

export const buildMenuSalesMixHeaderMetrics = ({
  isSalesMixTab = false,
  liveMetrics = null,
  currentMenu = {},
  menu = {},
} = {}) => {
  const mixSource =
    isSalesMixTab && liveMetrics != null
      ? liveMetrics
      : {
          hasSalesMix: currentMenu?.hasSalesMix ?? menu?.hasSalesMix,
          weightedGp: currentMenu?.weightedGp ?? menu?.weightedGp,
          estimatedTotalGp:
            currentMenu?.estimatedTotalGp ?? menu?.estimatedTotalGp,
        }

  return getMenuSalesMixDisplay(mixSource)
}

export const salesMixHasPositiveQty = (salesMix = []) =>
  (salesMix || []).some(entry => Number(entry?.expectedQty) > 0)

export const resolveSavedMenuAfterSalesMixSave = (
  menuId,
  response,
  { currentMenu, menus } = {},
) => {
  const candidates = [
    response?.menu,
    currentMenu && String(currentMenu._id) === String(menuId)
      ? currentMenu
      : null,
    (menus || []).find(menu => String(menu._id) === String(menuId)),
  ].filter(Boolean)

  return (
    candidates.find(
      menu => salesMixHasPositiveQty(menu.salesMix) || menu.hasSalesMix,
    ) ||
    candidates[0] ||
    null
  )
}

export const mapSalesMixToPanelQty = (panelRecipes = [], salesMix = []) => {
  const qtyByCanonicalId = new Map()

  for (const entry of salesMix || []) {
    const canonical = canonicalRecipeId(getRecipeId(entry))
    const qty = Number(entry?.expectedQty ?? entry?.expectedMonthlyQty)

    if (canonical && Number.isFinite(qty) && qty > 0) {
      qtyByCanonicalId.set(canonical, String(qty))
    }
  }

  const draft = {}
  for (const recipe of panelRecipes) {
    const panelId = recipe?.id
    const canonical = canonicalRecipeId(panelId)
    const qty = canonical ? qtyByCanonicalId.get(canonical) : null

    if (panelId && qty != null) {
      draft[panelId] = qty
    }
  }

  return draft
}

export const buildQtyDraftAfterSalesMixSave = ({
  savedSalesMix = [],
  entries = [],
  panelRecipes = [],
  qtyByRecipe = {},
} = {}) => {
  const fromSaved = mapSalesMixToPanelQty(panelRecipes, savedSalesMix)
  const local = pickQtyByRecipeForPanelRecipes(panelRecipes, qtyByRecipe)
  const merged = {}

  for (const recipe of panelRecipes) {
    const id = recipe.id
    if (!id) {
      continue
    }

    if (fromSaved[id] != null && fromSaved[id] !== '') {
      merged[id] = fromSaved[id]
      continue
    }

    if (local[id] != null && local[id] !== '') {
      merged[id] = local[id]
    }
  }

  if (Object.keys(merged).length > 0) {
    return merged
  }

  if (entries.some(entry => Number(entry?.expectedQty) > 0)) {
    return local
  }

  return fromSaved
}

export const toMenuSalesMixPanelRecipes = panelRecipes =>
  panelRecipes.map(({ id, title, sectionName, gpPercent, hasPricing }) => ({
    id,
    title,
    sectionName,
    gpPercent,
    hasPricing,
  }))
